import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function PermissionEditModal({
    isOpen,
    setIsModalOpen, 
    editingPermission,
    handleSubmit,
    formData,
    setFormData,
    modules = []
}){
    const [useExistingModule, setUseExistingModule] = useState(true);
    const [newModuleName, setNewModuleName] = useState('');

    // Use only real modules from backend API + existing permissions
    const availableModules = modules || [];

    // Reset states when modal opens/closes or editing changes
    React.useEffect(() => {
        if (isOpen) {
            if (editingPermission && editingPermission.module) {
                // If editing and module exists in the list, use existing
                const moduleExists = availableModules.includes(editingPermission.module);
                setUseExistingModule(moduleExists);
                if (!moduleExists) {
                    setNewModuleName(editingPermission.module);
                }
            } else {
                // If no modules available, default to new module input
                const hasAvailableModules = availableModules.length > 0;
                setUseExistingModule(hasAvailableModules);
                setNewModuleName('');
            }
        }
    }, [isOpen, editingPermission]); // Removed availableModules dependency to prevent input clearing

    // Update formData.module when switching between modes
    const handleModuleTypeChange = (useExisting) => {
        setUseExistingModule(useExisting);
        if (useExisting) {
            // Clear formData module, but preserve newModuleName for later use
            setFormData({ ...formData, module: '' });
        } else {
            // Switch to new module input and preserve existing newModuleName value
            setFormData({ ...formData, module: newModuleName });
        }
    };

    const handleNewModuleChange = (value) => {
        setNewModuleName(value);
        setFormData({ ...formData, module: value });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsModalOpen(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingPermission ? 'İcazəni Redaktə Et' : 'Yeni İcazə Əlavə Et'}
                  </h3>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    İcazə Adı *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="məs: users.create, students.view"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Görüntü Adı *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.display_name || ''}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="İstifadəçi əlavə etmək"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Modul *
                  </label>
                  
                  {/* Module type selection */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex space-x-6">
                      <label className={`flex items-center ${availableModules.length === 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                        <input
                          type="radio"
                          name="moduleType"
                          checked={useExistingModule}
                          onChange={() => handleModuleTypeChange(true)}
                          disabled={availableModules.length === 0}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">
                          Mövcud modullardan seç {availableModules.length === 0 && '(heç bir real modul yoxdur)'}
                        </span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="moduleType"
                          checked={!useExistingModule}
                          onChange={() => handleModuleTypeChange(false)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">Yeni modul daxil et</span>
                      </label>
                    </div>
                  </div>

                  {/* Conditional module input/selection */}
                  {useExistingModule ? (
                    <div>
                      <select
                        required
                        value={formData.module || ''}
                        onChange={(e) => setFormData({ ...formData, module: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        disabled={availableModules.length === 0}
                      >
                        <option value="">
                          {availableModules.length === 0 ? 'Heç bir modul tapılmadı' : 'Modul seçin'}
                        </option>
                        {availableModules.map((module) => (
                          <option key={module} value={module}>{module}</option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        {availableModules.length > 0 
                          ? `${availableModules.length} real modul (API + mövcud permission-lar)`
                          : 'Backend-dən modul yüklənmir və ya mövcud permission-larda modul yoxdur'
                        }
                      </p>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="text"
                        required
                        value={newModuleName}
                        onChange={(e) => handleNewModuleChange(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Yeni modul adını daxil edin..."
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Məsələn: dashboard, students, users, reports və s.
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Təsvir
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="İcazənin təsvirini daxil edin..."
                  />
                </div>


                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Ləğv et
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {editingPermission ? 'Yenilə' : 'Yadda saxla'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
    );
} 